import {
  AlignmentType,
  Document,
  ExternalHyperlink,
  Footer,
  HeadingLevel,
  ImageRun,
  Packer,
  PageNumber,
  Paragraph,
  TextRun
} from 'docx'
import moment from 'moment'

const DOCX_MIME_TYPE =
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document'

const normalizeText = value => {
  if (typeof value === 'string') return value.trim()
  return ''
}

const getDialogueText = dialogue => {
  if (!dialogue) return ''
  if (typeof dialogue === 'string') return dialogue.trim()
  if (Array.isArray(dialogue)) {
    return dialogue
      .map(line => {
        if (typeof line === 'string') return line.trim()
        if (line && typeof line === 'object') {
          const speaker = normalizeText(line.speaker || line.character || '')
          const text = normalizeText(
            line.text || line.dialogue || line.line || ''
          )
          return speaker && text ? `${speaker}: ${text}` : text
        }
        return ''
      })
      .filter(Boolean)
      .join('\n')
  }
  if (typeof dialogue === 'object') {
    return Object.entries(dialogue)
      .map(([speaker, text]) =>
        speaker && text ? `${speaker}: ${String(text).trim()}` : ''
      )
      .filter(Boolean)
      .join('\n')
  }
  return ''
}

const getShotImage = shot => {
  return (
    shot?.imageSrc ||
    shot?.image_url ||
    shot?.image ||
    shot?.thumbnail_image?.image_url ||
    shot?.thumbnailImage?.image_url ||
    shot?.thumbnailImage?.imageUrl ||
    shot?.thumbnail_image?.url ||
    ''
  )
}

const getShotVideoUrl = shot => {
  return (
    normalizeText(shot?.vidSrc) ||
    normalizeText(shot?.video_url) ||
    normalizeText(shot?.videoUrl) ||
    normalizeText(shot?.url) ||
    ''
  )
}

const getShotCaption = (shot, sceneIndex, shotIndex) => {
  const shotLabel = `Shot ${sceneIndex + 1}.${shotIndex + 1}`
  const caption =
    normalizeText(shot?.title) ||
    normalizeText(shot?.description) ||
    normalizeText(shot?.scenery) ||
    normalizeText(shot?.prompt) ||
    ''
  if (caption) return `${shotLabel}: ${caption}`
  return shotLabel
}

const getSceneHeading = (sceneTitle, sceneIndex) => {
  const trimmedTitle = normalizeText(sceneTitle)
  const sceneLabel = `Scene ${sceneIndex + 1}`
  if (!trimmedTitle) return sceneLabel
  const normalized = trimmedTitle
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim()
  const expected = sceneLabel.toLowerCase()
  if (normalized === expected || normalized === `scene ${sceneIndex + 1}:`) {
    return sceneLabel
  }
  return `${sceneLabel}: ${trimmedTitle}`
}

const fetchImageDataUrl = async url => {
  try {
    if (!url) return null
    const response = await fetch(url)
    if (!response.ok) return null
    const blob = await response.blob()
    return await new Promise(resolve => {
      const reader = new FileReader()
      reader.onloadend = () => resolve(reader.result)
      reader.readAsDataURL(blob)
    })
  } catch (error) {
    return null
  }
}

const getImageDimensions = imageData =>
  new Promise(resolve => {
    if (!imageData) {
      resolve(null)
      return
    }
    const img = new Image()
    img.onload = () => {
      resolve({
        width: img.naturalWidth || img.width,
        height: img.naturalHeight || img.height
      })
    }
    img.onerror = () => resolve(null)
    img.src = imageData
  })

const optimizeImageForDocument = async imageData => {
  if (!imageData || typeof imageData !== 'string') return null
  return new Promise(resolve => {
    const img = new Image()
    img.onload = () => {
      try {
        const maxWidth = 1300
        const targetWidth = Math.min(img.naturalWidth || img.width, maxWidth)
        const scale =
          targetWidth / (img.naturalWidth || img.width || targetWidth)
        const targetHeight = Math.max(
          1,
          Math.round((img.naturalHeight || img.height || 1) * scale)
        )
        const canvas = document.createElement('canvas')
        canvas.width = targetWidth
        canvas.height = targetHeight
        const ctx = canvas.getContext('2d')
        ctx.drawImage(img, 0, 0, targetWidth, targetHeight)
        resolve(canvas.toDataURL('image/jpeg', 0.84))
      } catch (error) {
        resolve(null)
      }
    }
    img.onerror = () => resolve(null)
    img.src = imageData
  })
}

const calculateDocumentImageSize = dimensions => {
  const maxWidth = 430
  const maxHeight = 190
  if (!dimensions?.width || !dimensions?.height) {
    return { width: maxWidth, height: maxHeight }
  }
  const scale = Math.min(
    maxWidth / dimensions.width,
    maxHeight / dimensions.height,
    1
  )
  return {
    width: Math.max(1, Math.round(dimensions.width * scale)),
    height: Math.max(1, Math.round(dimensions.height * scale))
  }
}

const buildTextParagraphs = (text, options = {}) => {
  const paragraphs = String(text || '')
    .split(/\n+/)
    .map(paragraph => paragraph.trim())
    .filter(Boolean)

  if (!paragraphs.length) {
    return [
      new Paragraph({
        spacing: { after: 180 },
        children: [
          new TextRun({
            text: options.fallback || 'No content available.',
            italics: true,
            color: '6B7280',
            size: options.size || 22
          })
        ]
      })
    ]
  }

  return paragraphs.map(
    paragraph =>
      new Paragraph({
        spacing: { after: options.after || 180 },
        children: [
          new TextRun({
            text: paragraph,
            size: options.size || 22,
            color: options.color || '111827'
          })
        ]
      })
  )
}

const buildSectionHeading = (text, options = {}) =>
  new Paragraph({
    heading: options.heading || HeadingLevel.HEADING_1,
    pageBreakBefore: !!options.pageBreakBefore,
    spacing: { before: options.before || 0, after: options.after || 160 },
    children: [
      new TextRun({
        text,
        bold: true,
        color: options.color || '14213D'
      })
    ]
  })

const buildLabelParagraph = label =>
  new Paragraph({
    spacing: { before: 120, after: 80 },
    children: [
      new TextRun({
        text: label,
        bold: true,
        size: 22,
        color: '1F2937'
      })
    ]
  })

const buildDividerParagraph = () =>
  new Paragraph({
    border: {
      bottom: {
        color: 'DCE1EB',
        space: 1,
        style: 'single',
        size: 6
      }
    },
    spacing: { after: 180 }
  })

const buildShotSpacerParagraph = () =>
  new Paragraph({
    spacing: { before: 160, after: 160 },
    children: []
  })

const buildCoverPage = () => [
  new Paragraph({ spacing: { before: 760, after: 240 }, children: [] }),
  new Paragraph({
    alignment: AlignmentType.CENTER,
    spacing: { after: 520 },
    children: [
      new TextRun({
        text: 'SHARELOOK',
        bold: true,
        size: 22,
        color: '1C2844'
      })
    ]
  }),
  new Paragraph({
    alignment: AlignmentType.CENTER,
    spacing: { after: 160 },
    children: [
      new TextRun({
        text: 'Video Storyboard',
        bold: true,
        size: 56,
        color: '1C2844'
      })
    ]
  }),
  buildDividerParagraph(),
  new Paragraph({
    alignment: AlignmentType.CENTER,
    spacing: { after: 1100 },
    children: [
      new TextRun({
        text: 'Storyboard Document',
        size: 24,
        color: '626D8A'
      })
    ]
  }),
  new Paragraph({
    alignment: AlignmentType.CENTER,
    children: [
      new TextRun({
        text: `Prepared on ${moment().format('MMMM D, YYYY')}`,
        size: 20,
        color: '5F6A81'
      })
    ]
  })
]

const buildVideoLinkParagraph = shotVideoUrl => {
  if (!shotVideoUrl) {
    return new Paragraph({
      spacing: { before: 60, after: 120 },
      children: [
        new TextRun({
          text: 'No video link available',
          italics: true,
          color: '6B7280'
        })
      ]
    })
  }

  return new Paragraph({
    spacing: { before: 60, after: 120 },
    children: [
      new ExternalHyperlink({
        link: shotVideoUrl,
        children: [
          new TextRun({
            text: 'Open video shot',
            style: 'Hyperlink'
          })
        ]
      })
    ]
  })
}

const buildShotBlocks = ({
  shotCaption,
  shotDialogue,
  shotVideoUrl,
  imageData,
  imageSize
}) => {
  const blocks = []

  if (imageData) {
    blocks.push(
      new Paragraph({
        alignment: AlignmentType.LEFT,
        spacing: { after: 60 },
        children: [
          new ImageRun({
            data: imageData,
            transformation: imageSize
          })
        ]
      }),
      new Paragraph({
        shading: {
          fill: 'E7ECFF'
        },
        spacing: { after: 90 },
        children: [
          new TextRun({
            text: shotCaption,
            bold: true,
            color: '2B3559',
            size: 18
          })
        ]
      })
    )
  } else {
    blocks.push(
      new Paragraph({
        alignment: AlignmentType.LEFT,
        spacing: { after: 110 },
        children: [
          new TextRun({
            text: 'Image preview unavailable',
            italics: true,
            color: '6B7280'
          })
        ]
      })
    )
  }

  blocks.push(
    buildLabelParagraph('Dialogue'),
    ...buildTextParagraphs(shotDialogue || 'No dialogue text for this shot.', {
      after: 90,
      size: 20
    }),
    buildVideoLinkParagraph(shotVideoUrl)
  )

  return blocks
}

const buildFooter = () =>
  new Footer({
    children: [
      new Paragraph({
        border: {
          top: {
            color: 'E8ECF3',
            space: 1,
            style: 'single',
            size: 6
          }
        },
        spacing: { before: 80 },
        children: [
          new TextRun({
            text: 'ShareLook',
            color: '788090',
            size: 18
          }),
          new TextRun({
            text: '     Page ',
            color: '788090',
            size: 18
          }),
          new TextRun({
            children: [PageNumber.CURRENT],
            color: '788090',
            size: 18
          })
        ]
      })
    ]
  })

export const generateStoryboardDocument = async ({
  item,
  fetchVideoById,
  onProgress,
  autoDownload = true
}) => {
  const notify = (status, progress) => {
    if (onProgress) onProgress({ status, progress })
  }

  notify('Fetching latest storyboard data...', 5)
  const detailsResponse = await fetchVideoById(item.id)
  const fullVideo = detailsResponse?.data?.data || detailsResponse?.data || item
  const storyText =
    normalizeText(fullVideo.voiceover_text) ||
    normalizeText(fullVideo.voiceoverText) ||
    'Story text is not available.'
  const scenes = Array.isArray(fullVideo.shot_list)
    ? fullVideo.shot_list
    : Array.isArray(fullVideo.shotList)
    ? fullVideo.shotList
    : []
  const totalShots = scenes.reduce(
    (sum, scene) =>
      sum + (Array.isArray(scene?.shots) ? scene.shots.length : 0),
    0
  )
  const progressTotal = Math.max(totalShots + scenes.length + 3, 1)
  let progressStep = 1
  const children = []

  const updateProgress = (status, increment = 1) => {
    progressStep += increment
    const nextProgress = Math.min(
      95,
      Math.round((progressStep / progressTotal) * 100)
    )
    notify(status, nextProgress)
  }

  children.push(
    ...buildCoverPage(),
    buildSectionHeading('Story', { pageBreakBefore: true }),
    buildDividerParagraph(),
    ...buildTextParagraphs(storyText, { after: 170 })
  )
  updateProgress('Adding story section...')

  for (let sceneIndex = 0; sceneIndex < scenes.length; sceneIndex += 1) {
    const scene = scenes[sceneIndex]
    const sceneTitle =
      normalizeText(scene.title) ||
      normalizeText(scene.id) ||
      `Scene ${sceneIndex + 1}`
    const sceneVoiceover =
      normalizeText(scene.aggregated_voiceover) ||
      normalizeText(scene.voiceover_text)
    const shots = Array.isArray(scene.shots) ? scene.shots : []
    const sceneHeading = getSceneHeading(sceneTitle, sceneIndex)

    children.push(
      buildSectionHeading(sceneHeading, { pageBreakBefore: true }),
      buildDividerParagraph(),
      buildLabelParagraph('Scene Narration'),
      ...buildTextParagraphs(
        sceneVoiceover || 'No scene narration available.',
        {
          after: 150
        }
      )
    )
    updateProgress(`Formatting Scene ${sceneIndex + 1} of ${scenes.length}...`)

    for (let shotIndex = 0; shotIndex < shots.length; shotIndex += 2) {
      if (shotIndex > 0) {
        children.push(
          buildSectionHeading(`${sceneHeading} (continued)`, {
            pageBreakBefore: true
          }),
          buildDividerParagraph()
        )
      }

      const shotsThisPage = shots.slice(shotIndex, shotIndex + 2)
      for (
        let localShotIndex = 0;
        localShotIndex < shotsThisPage.length;
        localShotIndex += 1
      ) {
        const currentShotIndex = shotIndex + localShotIndex
        const shot = shotsThisPage[localShotIndex]
        const shotCaption = getShotCaption(shot, sceneIndex, currentShotIndex)
        const shotDialogue = getDialogueText(shot.dialogue)
        const shotVideoUrl = getShotVideoUrl(shot)
        const imageDataRaw = await fetchImageDataUrl(getShotImage(shot))
        const imageData = await optimizeImageForDocument(imageDataRaw)
        const imageDimensions = await getImageDimensions(imageData)
        const imageSize = calculateDocumentImageSize(imageDimensions)

        notify(
          `Rendering Scene ${sceneIndex + 1}, Shot ${currentShotIndex + 1}...`
        )
        children.push(
          ...buildShotBlocks({
            shotCaption,
            shotDialogue,
            shotVideoUrl,
            imageData,
            imageSize
          })
        )
        if (localShotIndex < shotsThisPage.length - 1) {
          children.push(buildShotSpacerParagraph())
        }
        updateProgress('Preparing storyboard frames...')
      }
    }
  }

  const doc = new Document({
    sections: [
      {
        properties: {
          page: {
            margin: {
              top: 720,
              right: 720,
              bottom: 720,
              left: 720
            }
          }
        },
        footers: {
          default: buildFooter()
        },
        children
      }
    ]
  })
  const safeTitle = `sharelook-storyboard-${item.id ||
    moment().format('YYYYMMDDHHmmss')}`
  notify('Finalizing document...', 100)
  const fileName = `${safeTitle}.docx`
  const blob = await Packer.toBlob(doc)

  if (autoDownload) {
    const url = URL.createObjectURL(blob)
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = fileName
    document.body.appendChild(anchor)
    anchor.click()
    document.body.removeChild(anchor)
    URL.revokeObjectURL(url)
  }

  return {
    blob,
    fileName,
    format: 'document',
    mimeType: DOCX_MIME_TYPE
  }
}
